import ContentStorage, { ComponentKeys } from '../tnew/content/ContentStorage';
import Messenger from '../tnew/ui/Messenger';
import type FormHandler from './FormHandler';
import type { ControlGroupResultPair } from './FormSubmitHandler';
import { ValidationResult } from './ValidationService';

export default class FormErrorMessenger {
  messenger: Messenger;

  constructor(
    private formHandler: FormHandler,
    $el: Element,
    private headingLevel: number = 2,
  ) {
    if (headingLevel < 1 || headingLevel > 6) {
      throw new Error(`FormErrorMessenger headingLevel parameter is ${headingLevel}, should be between 0 and 7`);
    }

    $el.setAttribute('tabindex', '-1');

    this.messenger = new Messenger($el);
  }

  clear() {
    this.messenger.clearMessages();
  }

  focusOnFirstErrorAnchor() {
    const $anchor = this.messenger.$messageContainer.querySelector<HTMLAnchorElement>('ul li:first-child a');

    if ($anchor) {
      $anchor.focus();
    }
  }

  processValidationResults(validationResults: ValidationResult[]) {
    const sortedPairs = this.getSortedControlGroupsPairs(validationResults);

    let renderErrorList = false;

    sortedPairs.forEach(({ controlGroup, validationResult }) => {
      if (validationResult.isValid) {
        controlGroup.clearErrors();
      } else {
        controlGroup.showError(validationResult.message);

        renderErrorList = true;
      }
    });

    if (!renderErrorList) {
      return;
    }

    const lineItems = sortedPairs
      .filter(({ validationResult }) => !validationResult.isValid)
      .map(({ controlGroup, validationResult }) => {
        const { input } = controlGroup!;

        return `<li><a href="#${input.elementId}">${input.label || input.name}: ${validationResult.message}</a></li>`;
      });

    const errorHeadingId = 'tn-form-errors-alert-heading';
    const h = this.headingLevel;

    const html = `
      <h${h} id="${errorHeadingId}" class="tn-form-errors-alert__heading sr-only">Form Errors</h${h}>
      <div class="tn-form-errors-alert__message">${getUserMessage()}</div>
      <ul aria-labelledby="${errorHeadingId}" class="tn-form-errors-alert__list">
        ${lineItems.join('')}
      </ul>
    `;

    const { messenger } = this;

    messenger.addErrorMessage(html, false);

    this.focusOnFirstErrorAnchor();
  }

  private getSortedControlGroupsPairs(validationResults: ValidationResult[]) {
    const groups = validationResults.map((validationResult) => {
      const controlGroup = this.formHandler.getControlGroup(validationResult.fieldId);

      if (controlGroup) {
        return { controlGroup, validationResult };
      }

      return undefined;
    })
      .filter((el) => !!el!) as ControlGroupResultPair[];

    return groups.sort(sortControlGroupResultPairs);
  }
}

function getUserMessage() {
  return ContentStorage.get(ComponentKeys.FORM_FIELDS, 'FormSubmissionErrorMessage');
}

function sortControlGroupResultPairs(
  { controlGroup: { order: a } }: ControlGroupResultPair,
  { controlGroup: { order: b } }: ControlGroupResultPair,
) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}
